import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import Form from '../../../components/forms/Form'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import Button from '../../../components/Button'
import useForm from '../../../hooks/useForm'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { entities } from '../../../constants/entities'
import { cleanPayload, setDefaultFieldValues } from '../../../utils/objectUtils'
import { accountingFilterFields } from './filters'
import { useReport } from '../../../context/ReportContext'

const modeOptions = [
  { id: 'detailed', name: 'Detalhado' },
  { id: 'simple', name: 'Simplificado' },
]
const AccountingReport = () => {
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()
  const { setExportId, isExporting } = useReport()
  const [filtersSection] = accountingFilterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )
  const [hasStarted, setHasStarted] = useState(false)

  const organizationSelect = useRemoteSelectOptions({
    queryKey: ['accounting-report-organizations'],
    entityPath: entities.organizations.get,
    baseServiceGet,
    fieldId: 'filter.organization_ids',
    formData,
    setFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (organization) => ({
      value: organization.id,
      label: organization.alias || organization.name,
    }),
  })

  const modeSelect = useSelectOptions({
    fieldId: 'filter.mode',
    formData,
    setFormData,
    rawOptions: modeOptions,
    mapToOption: (option) => ({ value: option.id, label: option.name }),
  })

  const selects = {
    'filter.organization_ids': organizationSelect,
    'filter.mode': modeSelect,
  }

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [organizationSelect.options, modeSelect.options]
  )
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [organizationSelect.selectedValue, modeSelect.selectedValue]
  )
  const getSelectMeta = useCallback(
    (fieldId) => {
      const config = selects[fieldId]
      return config
        ? {
            hasNextPage: config.hasNextPage,
            isFetchingNextPage: config.isFetchingNextPage,
            isLoading: config.isLoading,
            isFetching: config.isFetching,
            handleMenuScrollToBottom: config.handleMenuScrollToBottom,
            handleSearch: config.handleSearch,
            onFocus: config.activateQuery,
          }
        : {}
    },
    [organizationSelect, modeSelect]
  )

  const generateReport = useMutation({
    mutationFn: async (outputFormat) => {
      showLoader()
      const filters = cleanPayload(formData.filter || {})
      const selectedMode = formData.filter?.mode?.value ?? formData.filter?.mode ?? 'detailed'
      const response = await baseServiceGet(
        entities.reports.accounting.get,
        {
          filters: {
            ...filters,
            organization_ids: filters.organization_ids?.map((item) => item.value) ?? [],
            mode: String(selectedMode).toLowerCase(),
          },
        },
        { type: outputFormat }
      )
      return response
    },
    onSuccess: (response) => {
      const exportId = response?.result?.export_id
      if (exportId) {
        setExportId(exportId)
        localStorage.setItem('report_exportId', exportId.toString())
        showNotification('info', 'O relatório contábil está sendo gerado.')
      }
    },
    onError: (error) => {
      console.error('Erro ao gerar relatório contábil:', error)
      showNotification('error', 'Não foi possível iniciar a geração do relatório.')
    },
    onSettled: hideLoader,
  })

  const fields = useMemo(() => filtersSection.fields, [filtersSection.fields])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Relatório Contábil" showBackButton={false} />
      <Alert severity="info" sx={{ mb: 2 }}>
        O ADI é apresentado como estoque físico global. Os saldos contábeis das
        organizações são calculados separadamente; a seleção de organizações não
        altera o total global do ADI. Para períodos extensos ou modo detalhado,
        prefira Excel; PDFs muito grandes podem exceder os limites de impressão.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          showButtons={false}
          disabledSubmitButton>
          {() => (
            <>
              <SectionHeader section={filtersSection.section} />
              <SimpleBody
                fields={fields}
                formErrors={{}}
                formData={formData}
                handleFieldChange={(fieldId, value) => {
                  handleChange(fieldId, value)
                  setHasStarted(true)
                }}
                getOptions={getOptions}
                getSelectedValue={getSelectedValue}
                getSelectMeta={getSelectMeta}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  gap: 1,
                  my: 4,
                }}>
                <Button
                  variant="outlined"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Gerar relatório em PDF"
                  disabled={isExporting || generateReport.isPending || !hasStarted}
                  onClick={() => generateReport.mutate('pdf')}
                />
                <Button
                  variant="contained"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Gerar relatório em Excel"
                  disabled={isExporting || generateReport.isPending || !hasStarted}
                  onClick={() => generateReport.mutate('excel')}
                  sx={{
                    backgroundColor: '#2e7d32',
                    borderColor: '#2e7d32',
                    '&:hover': {
                      backgroundColor: '#1b5e20',
                      borderColor: '#1b5e20',
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default AccountingReport
